// src/pages/AdminPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Alert,
  Table,
  DatePicker,
  Button,
  message,
  Tag,
  Space,
  InputNumber,
  Descriptions,
  Divider,
  Spin,
  Card,
  Statistic,
  Row,
  Col,
  Select,
} from "antd";
import axios from "../api/axios";
import dayjs from "dayjs";
import { euro } from "../utils/tariffs";

const COLORS = {
  primary: "#3FB6C2",
  primaryDark: "#1F4E57",
  primaryLight: "#E8F7F9",
  border: "#DCE7F3",
  text: "#1F2937",
  muted: "#6B7280",
};

export default function AdminPage() {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [details, setDetails] = useState({});
  const [loadingDetail, setLoadingDetail] = useState({});
  const [validUntilDraft, setValidUntilDraft] = useState({});
  const [overrideValues, setOverrideValues] = useState({});
  const [priceFeedback, setPriceFeedback] = useState({});
  const [pricingAvailable, setPricingAvailable] = useState(null);
  const [savingPrices, setSavingPrices] = useState({});
  const [contractDraft, setContractDraft] = useState({});

  const getPassengerCount = (detail, user) =>
    detail?.usedPassengers ??
    detail?.currentPassengerCount ??
    detail?.activePassengerCount ??
    detail?.usedPassengerCount ??
    detail?.passengerCount ??
    user?.usedPassengers ??
    user?.currentPassengerCount ??
    user?.activePassengerCount ??
    user?.usedPassengerCount ??
    user?.passengerCount ??
    0;

  const getBusCount = (detail, user) =>
    detail?.usedBuses ??
    detail?.currentBusCount ??
    detail?.activeBusCount ??
    detail?.usedBusCount ??
    detail?.busCount ??
    user?.usedBuses ??
    user?.currentBusCount ??
    user?.activeBusCount ??
    user?.usedBusCount ??
    user?.busCount ??
    0;

  const fetchUsers = async () => {
    setLoadingUsers(true);

    try {
      const res = await axios.get("/admin/users");
      setUsers(res.data || []);
    } catch {
      message.error("Fehler beim Laden der Nutzerliste");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchDetail = async (userId) => {
    setLoadingDetail((s) => ({ ...s, [userId]: true }));

    try {
      const subscriptionRes = await axios.get(`/admin/subscription/${userId}`);
      let data = subscriptionRes.data || {};

      try {
        const limitsRes = await axios.get(`/admin/users/${userId}/limits`);
        data = {
          ...data,
          ...(limitsRes.data || {}),
        };
      } catch {
        console.warn(
          `Keine Admin-Limits für User ${userId} unter /admin/users/${userId}/limits gefunden.`
        );
      }

      setDetails((d) => ({ ...d, [userId]: data }));

      setOverrideValues((ov) => ({
        ...ov,
        [userId]: {
          pax: data?.maxPassengersOverride ?? null,
        },
      }));

      setValidUntilDraft((vu) => ({
        ...vu,
        [userId]: data?.subscriptionValidUntil || null,
      }));
      setContractDraft((drafts) => ({
        ...drafts,
        [userId]: {
          validFrom: data?.subscriptionValidFrom || null,
          annualPrice: data?.accountAnnualPrice ?? null,
          monthlyPrice: data?.accountMonthlyPrice ?? null,
          billingPeriod: data?.billingPeriod || "ANNUAL",
        },
      }));
    } catch {
      message.error("Fehler beim Laden der Kundendetails");
    } finally {
      setLoadingDetail((s) => ({ ...s, [userId]: false }));
    }
  };

  useEffect(() => {
    fetchUsers();
    axios.get("/pricing").then(({ data }) => setPricingAvailable(data.modelVersion === "individual-account-pricing-v1"))
      .catch(() => setPricingAvailable(false));
  }, []);

  const saveValidUntil = async (userId) => {
    const newDateStr = validUntilDraft[userId] || null;

    try {
      await axios.post("/admin/subscription/setValidUntil", {
        userId,
        validUntil: newDateStr,
      });

      message.success("Ablaufdatum gespeichert.");
      await fetchDetail(userId);
      await fetchUsers();
    } catch {
      message.error("Konnte Ablaufdatum nicht speichern.");
    }
  };

  const saveOverrides = async (userId) => {
    const vals = overrideValues[userId] || {};

    try {
      await axios.post("/admin/subscription/overrides", {
        userId,
        maxPassengersOverride: vals.pax ?? null,
      });

      message.success("Overrides gespeichert.");
      await fetchDetail(userId);
      await fetchUsers();
    } catch {
      message.error("Konnte Overrides nicht speichern.");
    }
  };

  const clearOverrides = async (userId) => {
    try {
      await axios.delete(`/admin/subscription/overrides/${userId}`);

      message.success("Overrides entfernt.");
      await fetchDetail(userId);
      await fetchUsers();
    } catch {
      message.error("Konnte Overrides nicht entfernen.");
    }
  };

  const saveContract = async (userId) => {
    if (!pricingAvailable) {
      setPriceFeedback(all => ({ ...all, [userId]: { type: "error", text: "Die Preisfunktion fehlt im Serverbackend. Bitte das Backend-Update installieren und den Dienst neu starten." } }));
      return;
    }
    const draft = contractDraft[userId] || {};
    setSavingPrices(all => ({ ...all, [userId]: true }));
    setPriceFeedback(all => ({ ...all, [userId]: null }));
    try {
      await axios.post("/admin/subscription/account-pricing", {
        userId, pricingMode: "FLAT", annualPrice: draft.annualPrice ?? null,
        monthlyPrice: draft.monthlyPrice ?? null, billingPeriod: draft.billingPeriod || "ANNUAL",
      });
      await fetchDetail(userId);
      setPriceFeedback(all => ({ ...all, [userId]: { type: "success", text: "Individuelle Kundenpreise gespeichert." } }));
    } catch (failure) {
      const status = failure.response?.status;
      const text = [404, 405].includes(status)
        ? "Die Preisfunktion fehlt im Serverbackend. Bitte das Backend-Update installieren und den Dienst neu starten."
        : failure.response?.data?.message || "Kundenpreise konnten nicht gespeichert werden.";
      setPriceFeedback(all => ({ ...all, [userId]: { type: "error", text } }));
    } finally { setSavingPrices(all => ({ ...all, [userId]: false })); }
  };

  const renderTariffTag = (name = "JAHRESLIZENZ") => (
    <Tag
      style={{
        background: COLORS.primaryLight,
        color: COLORS.primaryDark,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 999,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {name}
    </Tag>
  );

  const renderStatus = (userRow) => {
    const expired =
      userRow.subscriptionValidUntil &&
      dayjs().isAfter(dayjs(userRow.subscriptionValidUntil));

    const paxHit = !!userRow.passengerLimitReached;

    const tags = [];

    if (expired) {
      tags.push(
        <Tag key="expired" color="red">
          Abo abgelaufen
        </Tag>
      );
    }

    if (paxHit) {
      tags.push(
        <Tag key="pax" color="orange">
          Fahrgast-Limit erreicht
        </Tag>
      );
    }

    if (tags.length === 0) {
      tags.push(
        <Tag key="ok" color="success">
          OK
        </Tag>
      );
    }

    return (
      <Space size={4} wrap>
        {tags}
      </Space>
    );
  };

  const columns = useMemo(
    () => [
      { title: "ID", dataIndex: "id", width: 80 },
      { title: "E-Mail", dataIndex: "email" },
      {
        title: "Gültig bis",
        dataIndex: "subscriptionValidUntil",
        width: 140,
        render: (val) => (val ? dayjs(val).format("DD.MM.YYYY") : "—"),
      },
      {
        title: "Status",
        key: "status",
        width: 300,
        render: (_, record) => renderStatus(record),
      },
      {
        title: "Tarif",
        dataIndex: "subscriptionType",
        width: 170,
        render: () => renderTariffTag(),
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  return (
    <div className="admin-page">
      <h2 className="app-page-title">Admin: Kundenverwaltung & Kapazitäten</h2>

      <Table
        rowKey="id"
        loading={loadingUsers}
        dataSource={users}
        columns={columns}
        pagination={{ pageSize: 12 }}
        bordered
        expandable={{
          expandedRowRender: (user) => {
            const d = details[user.id];
            const loading = !!loadingDetail[user.id];

            const currentPassengers = getPassengerCount(d, user);
            const currentBuses = getBusCount(d, user);
            const quote = d?.pricingQuote?.pricingMode === "FLAT" ? d.pricingQuote : null;
            const billablePassengers = quote?.passengerCount ?? d?.effectivePassengerLimit ?? currentPassengers;
            const effectiveAnnualPrice = quote?.annualPriceNet ?? null;

            return (
              <div className="detail-box">
                {!d && !loading && (
                  <Button onClick={() => fetchDetail(user.id)}>
                    Details laden
                  </Button>
                )}

                {loading && (
                  <div style={{ padding: 16 }}>
                    <Spin />
                  </div>
                )}

                {d && !loading && (
                  <>
                    <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                      <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                          <Statistic
                            title="Aktuelle Fahrgäste"
                            value={currentPassengers}
                          />
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                          <Statistic title="Aktuelle Busse" value={currentBuses} />
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                          <Statistic
                            title="Abgerechnete Fahrgäste"
                            value={billablePassengers}
                          />
                        </Card>
                      </Col>

                      <Col xs={24} sm={12} lg={6}>
                        <Card className="stat-card">
                          <Statistic
                            title="Individueller Jahrespreis netto"
                            value={effectiveAnnualPrice !== null ? euro.format(effectiveAnnualPrice) : "Individuell"}
                          />
                        </Card>
                      </Col>
                    </Row>

                    <Descriptions
                      title="Tarif & Nutzung"
                      size="small"
                      column={3}
                      style={{ marginBottom: 12 }}
                    >
                      <Descriptions.Item label="Tarif">
                        {renderTariffTag(`Smart Routing · ${billablePassengers} Fahrgäste`)}
                      </Descriptions.Item>

                      <Descriptions.Item label="Zahlungsweise">
                        <Select
                          aria-label="Zahlungsweise festlegen"
                          placeholder="Noch nicht hinterlegt"
                          value={d.billingPeriod || undefined}
                          style={{ width: "100%", minWidth: 170 }}
                          options={[{ value: "ANNUAL", label: "Jährlich im Voraus" }, { value: "MONTHLY", label: "Monatlich zahlen" }]}
                          onChange={async (billingPeriod) => {
                            try {
                              await axios.post("/admin/subscription/billing-period", { userId: user.id, billingPeriod });
                              await fetchDetail(user.id);
                              message.success("Zahlungsweise gespeichert.");
                            } catch { message.error("Zahlungsweise konnte nicht gespeichert werden."); }
                          }}
                        />
                      </Descriptions.Item>

                      <Descriptions.Item label="Preisart">
                        Individuell vereinbart
                      </Descriptions.Item>

                      <Descriptions.Item label={d?.effectiveAnnualPriceNet != null ? "Vereinbarter Jahrespreis" : "Individueller Jahrespreis"}>
                        {effectiveAnnualPrice !== null ? euro.format(effectiveAnnualPrice) : "Individuell"}
                      </Descriptions.Item>



                      {quote?.monthlyPriceNet != null && <Descriptions.Item label="Individueller Monatspreis">
                        {euro.format(quote.monthlyPriceNet)} netto/Monat
                      </Descriptions.Item>}

                      <Descriptions.Item label="Vertragsbeginn">
                        {d.subscriptionValidFrom ? dayjs(d.subscriptionValidFrom).format("DD.MM.YYYY") : "—"}
                      </Descriptions.Item>

                      <Descriptions.Item label="Gültig bis">
                        {d.subscriptionValidUntil
                          ? dayjs(d.subscriptionValidUntil).format("DD.MM.YYYY")
                          : "—"}
                      </Descriptions.Item>

                      <Descriptions.Item label="Status">
                        {d.subscriptionExpired ||
                        (d.subscriptionValidUntil &&
                          dayjs().isAfter(dayjs(d.subscriptionValidUntil))) ? (
                          <Tag color="red">abgelaufen</Tag>
                        ) : (
                          <Tag color="success">aktiv</Tag>
                        )}
                      </Descriptions.Item>

                      <Descriptions.Item label="Effektive Limits">
                        Fahrgäste {d.effectivePassengerLimit ?? "—"}
                      </Descriptions.Item>


                    </Descriptions>

                    <Divider style={{ margin: "12px 0" }} />

                    <Space
                      direction="vertical"
                      size="middle"
                      style={{ width: "100%" }}
                    >
                      <Space wrap align="center">
                        <span style={{ fontWeight: 600 }}>Abo gültig bis:</span>

                        <DatePicker
                          value={
                            validUntilDraft[user.id]
                              ? dayjs(validUntilDraft[user.id])
                              : d.subscriptionValidUntil
                              ? dayjs(d.subscriptionValidUntil)
                              : null
                          }
                          format="DD.MM.YYYY"
                          onChange={(date) =>
                            setValidUntilDraft((vu) => ({
                              ...vu,
                              [user.id]: date ? date.format("YYYY-MM-DD") : null,
                            }))
                          }
                          placeholder="Kein Ablaufdatum"
                        />

                        <Button
                          type="primary"
                          onClick={() => saveValidUntil(user.id)}
                        >
                          Ablaufdatum speichern
                        </Button>
                      </Space>

                      <Card size="small" title="Individuelle Kundenpreise">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Space wrap>
                            <label>Jahrespreis <InputNumber aria-label={`Jahrespreis für ${user.email}`} min={0} max={999999999999.99} precision={2} decimalSeparator="," inputMode="decimal" addonAfter="€ netto/Jahr"
                              value={contractDraft[user.id]?.annualPrice} onChange={value => setContractDraft(all => ({ ...all, [user.id]: { ...all[user.id], annualPrice: value } }))} /></label>
                            <label>Monatspreis <InputNumber aria-label={`Monatspreis für ${user.email}`} min={0} max={999999999999.99} precision={2} decimalSeparator="," inputMode="decimal" addonAfter="€ netto/Monat"
                              value={contractDraft[user.id]?.monthlyPrice} onChange={value => setContractDraft(all => ({ ...all, [user.id]: { ...all[user.id], monthlyPrice: value } }))} /></label>
                            <Select aria-label={`Zahlungsweise für ${user.email}`} style={{ width: 180 }} value={contractDraft[user.id]?.billingPeriod || "ANNUAL"}
                              onChange={value => setContractDraft(all => ({ ...all, [user.id]: { ...all[user.id], billingPeriod: value } }))}
                              options={[{ value: "ANNUAL", label: "Jährlich" }, { value: "MONTHLY", label: "Monatlich" }]} />
                          </Space>
                          <div className="hint">Individuelle Gesamtpreise für die freigegebene Kapazität. Leere Felder bedeuten „noch nicht vereinbart“, 0 € ist ein ausdrücklich kostenloser Preis. Kein automatischer Aufpreis oder Standardpreis.</div>
                          {pricingAvailable === false && <Alert type="error" showIcon message="Die Preisfunktion fehlt im Serverbackend. Bitte das Backend-Update installieren und den Dienst neu starten." />}
                          {priceFeedback[user.id] && <Alert showIcon type={priceFeedback[user.id].type} message={priceFeedback[user.id].text} />}
                          <Button type="primary" disabled={pricingAvailable !== true} loading={Boolean(savingPrices[user.id])} onClick={() => saveContract(user.id)}>Kundenpreise speichern</Button>
                        </Space>
                      </Card>

                      <Space wrap align="center">
                        <InputNumber
                          min={0}
                          style={{ width: 220 }}
                          placeholder="Individuelles Fahrgastlimit"
                          max={2147483647} precision={0}
                          value={overrideValues[user.id]?.pax}
                          onChange={(val) =>
                            setOverrideValues((ov) => ({
                              ...ov,
                              [user.id]: {
                                ...ov[user.id],
                                pax: val,
                              },
                            }))
                          }
                        />

                        <Button
                          type="primary"
                          onClick={() => saveOverrides(user.id)}
                        >
                          Grenzen speichern
                        </Button>

                        <Button danger onClick={() => clearOverrides(user.id)}>
                          Standardgrenzen wiederherstellen
                        </Button>
                      </Space>

                      <Space direction="vertical" size={6}>
                        <div>
                          Aktuelle Overrides:&nbsp;
                          <Tag
                            className={
                              d.maxPassengersOverride != null ? "custom-tag" : ""
                            }
                          >
                            Fahrgäste:{" "}
                            {d.maxPassengersOverride != null
                              ? d.maxPassengersOverride
                              : "—"}
                          </Tag>

                        </div>

                        <div className="hint">
                          Preis und Fahrgastlimit werden für diesen Kunden individuell festgelegt. Fahrzeuge sind tariflich unbegrenzt.
                        </div>

                        {d.effectivePassengerLimit === 0 && (
                          <Tag color="orange">
                            Hinweis: Keine Fahrgäste freigeschaltet.
                          </Tag>
                        )}
                      </Space>
                    </Space>
                  </>
                )}
              </div>
            );
          },

          onExpand: (expanded, record) => {
            if (expanded && !details[record.id]) {
              fetchDetail(record.id);
            }
          },
        }}
      />

      <style>{`
        .admin-page h2 {
          margin-top: 0;
          margin-bottom: 18px;
          color: ${COLORS.text};
          font-weight: 700;
        }

        .detail-box {
          background: #FFFFFF;
          border: 1px solid ${COLORS.border};
          border-radius: 14px;
          padding: 16px;
        }

        .stat-card {
          border-radius: 14px;
          border: 1px solid ${COLORS.border};
          background: #F8FCFD;
        }

        .admin-page .ant-statistic-title {
          color: ${COLORS.muted};
          font-weight: 600;
        }

        .admin-page .ant-statistic-content {
          color: ${COLORS.primaryDark};
          font-weight: 700;
        }

        .admin-page .ant-table-thead > tr > th {
          white-space: nowrap;
          background: #F4FBFC !important;
          border-bottom: 1px solid ${COLORS.border} !important;
          color: ${COLORS.text} !important;
          font-weight: 700 !important;
        }

        .admin-page .ant-table-tbody > tr > td {
          vertical-align: top;
          line-height: 1.45;
        }

        .admin-page .ant-descriptions-title {
          color: ${COLORS.text};
          font-weight: 700;
        }

        .admin-page .tier-price-description .ant-descriptions-item-container {
          align-items: flex-start;
          flex-direction: column;
        }

        .admin-page .tier-price-description .ant-descriptions-item-content {
          white-space: nowrap;
        }

        .custom-tag {
          background: ${COLORS.primaryLight};
          color: ${COLORS.primaryDark};
          border: 1px solid ${COLORS.border};
        }

        .hint {
          max-width: 760px;
          color: ${COLORS.muted};
          font-size: 13px;
          line-height: 1.5;
        }

        .admin-page .ant-btn-primary {
          background: ${COLORS.primary};
          border: none;
          font-weight: 600;
        }

        .admin-page .ant-btn-primary:hover {
          background: ${COLORS.primaryDark};
        }
      `}</style>
    </div>
  );
}
